import { useEffect, useRef, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Conversation header title with an optional inline rename affordance.
 *
 * Rename is offered only when `canEdit` is true — the caller gates this on the thread's contact
 * not yet being synced/merged with a Plato patient (see `headerRenameTarget` in use-messages-page).
 * Saving calls `onSave(name)`, which renames the underlying contact; for a 1:1 that contact's
 * display name is what drives this title, so the change is reflected here and everywhere the
 * contact appears. `children` (e.g. the online dot) renders after the title in display mode.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.secondary] identity fact not already in the title (push name next to a
 *   phone title, phone next to a push-name title) — muted "(…)" suffix, display mode only
 * @param {boolean} [props.isPlaceholder]
 * @param {boolean} [props.canEdit]
 * @param {(name: string) => void} [props.onSave]
 * @param {import("react").ReactNode} [props.children]
 */
export function EditableConversationTitle({
  title,
  secondary = "",
  isPlaceholder = false,
  canEdit = false,
  onSave,
  editHint = "Rename (not yet synced with a Plato patient)",
  children,
}) {
  const [editing, setEditing] = useState(false);
  // A placeholder label ("unnamed"/"default") isn't a real name, so start the editor empty.
  const seed = isPlaceholder ? "" : title;
  const [value, setValue] = useState(seed);
  const inputRef = useRef(null);

  // Keep the draft in sync with the source title while not editing (e.g. after a refresh).
  useEffect(() => {
    if (!editing) setValue(seed);
  }, [seed, editing]);

  // Focus + select the whole title when entering edit mode.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const cancel = () => {
    setValue(seed);
    setEditing(false);
  };

  const commit = () => {
    const next = String(value || "").trim();
    setEditing(false);
    if (next && next !== String(seed || "").trim()) {
      void onSave?.(next);
    } else {
      setValue(seed);
    }
  };

  if (editing) {
    return (
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          onBlur={cancel}
          aria-label="Edit conversation title"
          maxLength={120}
          className="h-7 max-w-[16rem] text-base font-semibold"
        />
        {/* preventDefault on mousedown keeps the input focused so onBlur doesn't fire before the click */}
        <button
          type="button"
          aria-label="Save title"
          onMouseDown={(e) => e.preventDefault()}
          onClick={commit}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-emerald-500/50 text-emerald-600 transition hover:bg-emerald-500/10 dark:border-emerald-400/45 dark:text-emerald-300"
        >
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Cancel rename"
          onMouseDown={(e) => e.preventDefault()}
          onClick={cancel}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </span>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className={cn("truncate", isPlaceholder ? "text-muted-foreground" : undefined)}>{title}</span>
      {secondary ? (
        <span
          className="min-w-0 shrink-[2] truncate text-sm font-normal text-muted-foreground"
          title={secondary}
        >
          ({secondary})
        </span>
      ) : null}
      {children}
      {canEdit ? (
        <button
          type="button"
          aria-label="Rename conversation"
          title={editHint}
          onClick={() => setEditing(true)}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border/60 text-muted-foreground opacity-70 transition hover:bg-muted/60 hover:text-foreground hover:opacity-100 focus-visible:opacity-100"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </span>
  );
}
